import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  Skeleton,
  Stepper,
  Step,
  StepLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  ShoppingCart,
  LocalShipping,
  Payment,
  Person,
  CheckCircle,
  ArrowBack,
  CreditCard,
  AccountBalance,
  Money,
  Phone,
  Email,
  LocationOn
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Checkout = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [user, setUser] = useState(null);

  // Form states
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    mobile: '',
    email: ''
  });
  const [shippingAddress, setShippingAddress] = useState({
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'India'
  });
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showAddAddressForm, setShowAddAddressForm] = useState(false);
  const [paymentMode, setPaymentMode] = useState('cash');
  const [notes, setNotes] = useState('');

  // Get checkout mode and identifiers from URL params
  const getCheckoutConfig = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    const storeId = urlParams.get('storeId');

    return { mode, storeId };
  };

  const { mode, storeId } = getCheckoutConfig();

  const steps = ['Review Order', 'Customer Details', 'Shipping & Payment', 'Confirmation'];

  // Check if user is authenticated
  useEffect(() => {
    const checkAuth = () => {
      const businessToken = localStorage.getItem('authToken');
      const businessUserData = localStorage.getItem('userData');
      const customerToken = localStorage.getItem('customerToken');
      const customerUserData = localStorage.getItem('customerData');

      if (businessToken && businessUserData) {
        try {
          const parsedUser = JSON.parse(businessUserData);
          console.log('🛒 CHECKOUT: Found business owner:', parsedUser.name);
          setUser(parsedUser);
        } catch (error) {
          console.error('🛒 CHECKOUT: Error parsing business user data:', error);
        }
      } else if (customerToken && customerUserData) {
        try {
          const parsedUser = JSON.parse(customerUserData);
          console.log('🛒 CHECKOUT: Found customer:', parsedUser.name);
          setUser(parsedUser);
        } catch (error) {
          console.error('🛒 CHECKOUT: Error parsing customer user data:', error);
        }
      } else {
        console.log('🛒 CHECKOUT: No user authentication found');
        setUser(null);
      }
    };

    checkAuth();

    // Listen for storage changes (login/logout from other tabs)
    const handleStorageChange = (e) => {
      if (e.key === 'authToken' || e.key === 'userData' || e.key === 'customerToken' || e.key === 'customerData') {
        console.log('🛒 CHECKOUT: Auth storage changed, re-checking auth');
        checkAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    // For customer mode, wait for user authentication
    if (mode === 'customer') {
      if (user && user.id) {
        fetchCart();
      }
    } else {
      // For other modes, load cart immediately
      fetchCart();
    }
  }, [mode, storeId, user]);

  const loadCustomerAddresses = async () => {
    if (!user || !user.id) return;

    try {
      const token = localStorage.getItem('customerToken');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/customer/addresses`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.addresses) {
        setSavedAddresses(response.data.addresses);
        // Auto-select the default address if available
        const defaultAddress = response.data.addresses.find(addr => addr.is_default);
        if (defaultAddress) {
          setSelectedAddressId(defaultAddress.id);
          setShippingAddress({
            address: defaultAddress.address || '',
            city: defaultAddress.city || '',
            state: defaultAddress.state || '',
            zipCode: defaultAddress.pincode || '',
            country: 'India'
          });
        } else if (response.data.addresses.length > 0) {
          // If no default, select the first address
          const firstAddress = response.data.addresses[0];
          setSelectedAddressId(firstAddress.id);
          setShippingAddress({
            address: firstAddress.address || '',
            city: firstAddress.city || '',
            state: firstAddress.state || '',
            zipCode: firstAddress.pincode || '',
            country: 'India'
          });
        }
      }
    } catch (error) {
      console.error('Error loading customer addresses:', error);
      // Fallback to user.address if API fails
      if (user && user.address) {
        // Fallback to single address field for backward compatibility
        // Try to parse the address if it contains structured data
        let address = user.address;
        let city = user.preferred_city || '';
        let state = '';
        let zipCode = user.preferred_pincode || '';

        // If address contains commas, try to split it
        if (address && address.includes(',')) {
          const parts = address.split(',').map(part => part.trim());
          if (parts.length >= 3) {
            address = parts.slice(0, -2).join(', '); // Everything except last 2 parts
            city = parts[parts.length - 2] || city;
            state = parts[parts.length - 1] || state;
          }
        }

        setShippingAddress({
          address: address || '',
          city: city,
          state: state,
          zipCode: zipCode,
          country: 'India'
        });
      }
    }
  };

  // Auto-fill customer details when user is authenticated in customer mode
  useEffect(() => {
    if (mode === 'customer' && user && user.id) {
      setCustomerInfo({
        name: user.name || '',
        mobile: user.mobile || '',
        email: user.email || ''
      });

      // Load saved addresses from customer profile API
      loadCustomerAddresses();
    }
  }, [mode, user]);

  // Reload cart when localStorage changes (e.g., cart updates from other tabs)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key && e.key.startsWith('cart_')) {
        console.log('🛒 CHECKOUT: Cart storage changed, reloading cart');
        fetchCart();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken') || localStorage.getItem('customerToken');

      console.log('🛒 CHECKOUT: Starting fetchCart');
      console.log('🛒 CHECKOUT: URL params - mode:', mode, 'storeId:', storeId);
      console.log('🛒 CHECKOUT: User authenticated:', user ? `${user.name} (${user.id})` : 'null');

      // For customer mode, ensure we have proper authentication before proceeding
      if (mode === 'customer' && !user) {
        console.log('🛒 CHECKOUT: Customer mode but no user authenticated yet, waiting...');
        setLoading(false);
        return;
      }

      // Use customer-specific cart key for authenticated customers
      // This matches ProductCatalog.js and ShoppingCart.js logic
      let cartKey;
      if (user && user.id) {
        // Authenticated customer - use customer ID for cart persistence
        cartKey = `cart_customer_${user.id}`;
        console.log('🛒 CHECKOUT: Customer cart detected, using key:', cartKey);
      } else if (storeId) {
        // Single store mode for unauthenticated users
        cartKey = `cart_store_${storeId}`;
        console.log('🛒 CHECKOUT: Store mode detected, using key:', cartKey);
      } else {
        // Full marketplace mode for unauthenticated users
        cartKey = 'cart_marketplace';
        console.log('🛒 CHECKOUT: Marketplace cart, using key:', cartKey);
      }

      console.log('🛒 CHECKOUT: Final cart key:', cartKey);

      // Load from localStorage
      const savedCart = localStorage.getItem(cartKey);
      console.log('🛒 CHECKOUT: Raw saved cart data:', savedCart);

      if (savedCart && savedCart !== 'null' && savedCart !== 'undefined' && savedCart !== '') {
        try {
          const parsedCart = JSON.parse(savedCart);
          console.log('🛒 CHECKOUT: Parsed cart:', parsedCart);
          console.log('🛒 CHECKOUT: Cart length:', parsedCart ? parsedCart.length : 0);
          setCart(Array.isArray(parsedCart) ? parsedCart : []);
        } catch (parseError) {
          console.error('🛒 CHECKOUT: Error parsing cart data:', parseError);
          setCart([]);
        }
      } else {
        console.log('🛒 CHECKOUT: No valid saved cart found');
        setCart([]);
      }

      // For authenticated users, also try to get cart from server (optional)
      if (token && user && user.id) {
        try {
          console.log('🛒 CHECKOUT: Fetching cart from server for authenticated user');
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/cart`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.success && response.data.cart && response.data.cart.length > 0) {
            console.log('🛒 CHECKOUT: Server cart found, using server cart');
            setCart(response.data.cart);
            // Also save to localStorage to keep it in sync
            localStorage.setItem(cartKey, JSON.stringify(response.data.cart));
          } else {
            console.log('🛒 CHECKOUT: Server returned empty cart or no cart data');
          }
        } catch (serverErr) {
          console.log('🛒 CHECKOUT: Server cart not available, using local cart:', serverErr.message);
        }
      }

    } catch (err) {
      console.error('Error fetching cart:', err);
      setCart([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddressSelect = (addressId) => {
    const selectedAddress = savedAddresses.find(addr => (addr.id || addr.index) === addressId);
    if (selectedAddress) {
      setSelectedAddressId(addressId);
      setShippingAddress({
        address: selectedAddress.address || '',
        city: selectedAddress.city || '',
        state: selectedAddress.state || '',
        zipCode: selectedAddress.zipCode || selectedAddress.pincode || '',
        country: selectedAddress.country || 'India'
      });
      setShowAddAddressForm(false);
    }
  };

  const handleAddNewAddress = () => {
    setSelectedAddressId(null);
    setShippingAddress({
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'India'
    });
    setShowAddAddressForm(true);
  };

  const handleSaveNewAddress = async () => {
    if (!shippingAddress.address || !shippingAddress.city || !shippingAddress.state || !shippingAddress.zipCode) {
      setError('Please fill in all address fields');
      return;
    }

    try {
      const token = localStorage.getItem('customerToken');
      const newAddress = {
        type: 'Home',
        address: shippingAddress.address,
        city: shippingAddress.city,
        state: shippingAddress.state,
        pincode: shippingAddress.zipCode,
        isDefault: savedAddresses.length === 0 // Make first address default
      };

      // Save to backend
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/customer/addresses`, newAddress, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Update local state
        const updatedAddresses = [...savedAddresses, response.data.address];
        setSavedAddresses(updatedAddresses);
        setSelectedAddressId(response.data.address.id);
        setShowAddAddressForm(false);
      }
    } catch (error) {
      console.error('Error saving address:', error);
      setError('Failed to save address. Please try again.');
    }
  };

  const getCartTotal = () => {
    return (cart || []).reduce((total, item) => total + item.total, 0);
  };

  const getShippingCost = () => {
    // Free shipping over ₹500, otherwise ₹50
    return getCartTotal() >= 500 ? 0 : 50;
  };

  const getGrandTotal = () => {
    return getCartTotal() + getShippingCost();
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const validateStep = (step) => {
    switch (step) {
      case 1: // Customer Details
        return customerInfo.name.trim() && customerInfo.mobile.trim();
      case 2: // Shipping & Payment
        return shippingAddress.address.trim() &&
               shippingAddress.city.trim() &&
               shippingAddress.state.trim() &&
               shippingAddress.zipCode.trim();
      default:
        return true;
    }
  };

  const handlePlaceOrder = async () => {
    try {
      setProcessing(true);
      setError(null);

      const token = localStorage.getItem('authToken') || localStorage.getItem('customerToken');

      // For marketplace mode, require customer authentication
      if (mode === 'marketplace' && !token) {
        setError('Please sign in as a customer to place your order. This helps us deliver your products and track your orders.');
        // Redirect to customer auth
        setTimeout(() => {
          // Store current cart and redirect to auth, then back to checkout
          sessionStorage.setItem('pendingCheckout', JSON.stringify({
            mode,
            storeId,
            customerInfo,
            shippingAddress,
            paymentMode,
            notes
          }));
          navigate('/?auth=customer');
        }, 2000);
        return;
      }

      // Ensure user is authenticated for business mode
      if (!token) {
        setError('Authentication required to place an order. Please sign in.');
        return;
      }

      // Use the current cart state instead of localStorage to ensure we have the latest data
      // The cart state is updated with server data in fetchCart()
      const cartItems = cart || [];

      if (cartItems.length === 0) {
        setError('Your cart is empty. Please add items before checkout.');
        return;
      }

      // Transform cart items to the format expected by the orders API
      const orderItems = cartItems.map(item => ({
        productId: item.productId || item.id,
        quantity: item.quantity
      }));

      const orderData = {
        items: orderItems,
        shippingAddress: `${shippingAddress.address}, ${shippingAddress.city}, ${shippingAddress.state} ${shippingAddress.zipCode}, ${shippingAddress.country}`,
        paymentMethod: paymentMode,
        notes
      };

      const response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/orders`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const order = response.data.order;
        setOrderDetails(order);
        setOrderPlaced(true);

        // Clear cart from both localStorage and server (for authenticated users)
        let clearCartKey;
        if (user && user.id) {
          // Authenticated customer - clear both localStorage and server cart
          clearCartKey = `cart_customer_${user.id}`;
          localStorage.removeItem(clearCartKey);

          // Also clear server cart for authenticated users
          try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_API_URL}/cart`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            console.log('🛒 CHECKOUT: Server cart cleared for authenticated user');
          } catch (cartClearError) {
            console.warn('🛒 CHECKOUT: Failed to clear server cart:', cartClearError.message);
            // Don't fail the order if server cart clearing fails
          }
        } else if (storeId) {
          // Single store mode for unauthenticated users
          clearCartKey = `cart_store_${storeId}`;
          localStorage.removeItem(clearCartKey);
        } else {
          // Full marketplace mode for unauthenticated users
          clearCartKey = 'cart_marketplace';
          localStorage.removeItem(clearCartKey);
        }

        setCart([]); // Clear the cart state as well

        // Generate invoice automatically after order placement
        try {
          const invoiceResponse = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/invoice/generate`, {
            sheet_id: order.id, // Use order ID as sheet_id for invoice generation
            orderId: order.id,
            selectedTemplate: 'simple'
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (invoiceResponse.data.success) {
            // Update order details with invoice information
            setOrderDetails(prev => ({
              ...prev,
              invoice: invoiceResponse.data.invoice
            }));
          }
        } catch (invoiceError) {
          console.warn('Invoice generation failed:', invoiceError);
          // Don't fail the order if invoice generation fails
        }

        setActiveStep(3); // Go to confirmation step
      }
    } catch (err) {
      console.error('Error placing order:', err);
      setError(err.response?.data?.message || 'Failed to place order. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const OrderReview = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingCart />
          Order Review
        </Typography>
        <Divider sx={{ my: 2 }} />

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="center">Quantity</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(cart || []).map((item) => (
                <TableRow key={item.productId}>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">{item.product.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        SKU: {item.product.sku}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">₹{item.unitPrice.toFixed(2)}</TableCell>
                  <TableCell align="center">{item.quantity}</TableCell>
                  <TableCell align="right">₹{item.total.toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography>Subtotal</Typography>
          <Typography>₹{getCartTotal().toFixed(2)}</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography>Shipping</Typography>
          <Box>
            {getShippingCost() === 0 ? (
              <Chip label="FREE" color="success" size="small" />
            ) : (
              <Typography>₹{getShippingCost().toFixed(2)}</Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Grand Total</Typography>
          <Typography variant="h6" color="primary">
            ₹{getGrandTotal().toFixed(2)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  const CustomerDetailsForm = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person />
          Customer Details
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Full Name"
              value={customerInfo.name}
              onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
              required
              InputProps={{
                startAdornment: <InputAdornment position="start"><Person /></InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Mobile Number"
              value={customerInfo.mobile}
              onChange={(e) => setCustomerInfo({ ...customerInfo, mobile: e.target.value })}
              required
              InputProps={{
                startAdornment: <InputAdornment position="start"><Phone /></InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={customerInfo.email}
              onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Email /></InputAdornment>,
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const ShippingPaymentForm = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalShipping />
              Shipping Address
            </Typography>
            <Divider sx={{ my: 2 }} />

            {/* Address Selection for authenticated customers */}
            {mode === 'customer' && user && user.id && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Select Delivery Address
                </Typography>

                {/* Saved Addresses */}
                {savedAddresses.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <RadioGroup
                      value={selectedAddressId || ''}
                      onChange={(e) => handleAddressSelect(e.target.value)}
                    >
                      {savedAddresses.map((address, index) => (
                        <FormControlLabel
                          key={address.id || index}
                          value={address.id || index}
                          control={<Radio />}
                          label={
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {address.type || 'Home'}
                                </Typography>
                                {address.is_default && (
                                  <Chip label="Default" size="small" color="success" variant="outlined" />
                                )}
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {address.address}, {address.city}, {address.state} {address.pincode}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </Box>
                )}

                {/* Add New Address Option */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant={showAddAddressForm ? "contained" : "outlined"}
                    onClick={showAddAddressForm ? () => setShowAddAddressForm(false) : handleAddNewAddress}
                    startIcon={<LocationOn />}
                    size="small"
                  >
                    {showAddAddressForm ? 'Cancel' : '+ Add New Address'}
                  </Button>
                  {showAddAddressForm && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSaveNewAddress}
                      size="small"
                    >
                      Save Address
                    </Button>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />
              </Box>
            )}

            {/* Address Form */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={3}
                  value={shippingAddress.address}
                  onChange={(e) => setShippingAddress({ ...shippingAddress, address: e.target.value })}
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LocationOn /></InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={shippingAddress.city}
                  onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="State"
                  value={shippingAddress.state}
                  onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={shippingAddress.zipCode}
                  onChange={(e) => setShippingAddress({ ...shippingAddress, zipCode: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={shippingAddress.country}
                  onChange={(e) => setShippingAddress({ ...shippingAddress, country: e.target.value })}
                  required
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Payment />
              Payment Method
            </Typography>
            <Divider sx={{ my: 2 }} />

            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <RadioGroup
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
              >
                <FormControlLabel
                  value="cash"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Money />
                      <Box>
                        <Typography variant="body1">Cash on Delivery</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Pay when you receive your order
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="card"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CreditCard />
                      <Box>
                        <Typography variant="body1">Credit/Debit Card</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Pay securely with your card
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="bank"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccountBalance />
                      <Box>
                        <Typography variant="body1">Bank Transfer</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Direct bank transfer
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            <TextField
              fullWidth
              label="Order Notes (Optional)"
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any special instructions for delivery..."
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const OrderConfirmation = () => (
    <Card sx={{ textAlign: 'center', py: 4 }}>
      <CardContent>
        <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom color="success.main">
          Order Placed Successfully!
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Thank you for your order. We'll send you a confirmation email shortly.
        </Typography>

        {orderDetails && (
          <Box sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto', mb: 3 }}>
            <Typography variant="h6" gutterBottom>Order Details</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>Order Number:</Typography>
              <Typography sx={{ fontWeight: 600 }}>{orderDetails.orderNumber}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>Total Amount:</Typography>
              <Typography sx={{ fontWeight: 600 }}>₹{orderDetails.totalAmount?.toFixed(2) || '0.00'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>Payment Mode:</Typography>
              <Typography sx={{ fontWeight: 600 }}>{orderDetails.paymentMethod?.toUpperCase() || paymentMode.toUpperCase()}</Typography>
            </Box>
            {orderDetails.invoice && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Invoice Generated</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => window.open(orderDetails.invoice.downloadUrl, '_blank')}
                  >
                    Download Invoice
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => window.open(orderDetails.invoice.printUrl, '_blank')}
                  >
                    Print Invoice
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={() => {
              // Navigate back to products - ProductCatalog will handle correct mode based on user/store context
              navigate('/products');
            }}
          >
            Continue Shopping
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/orders')}
          >
            View Orders
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <OrderReview />;
      case 1:
        return <CustomerDetailsForm />;
      case 2:
        return <ShippingPaymentForm />;
      case 3:
        return <OrderConfirmation />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Checkout</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Skeleton variant="text" height={40} />
                <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if ((cart || []).length === 0 && !orderPlaced) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Checkout</Typography>
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <CardContent>
            <ShoppingCart sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Your cart is empty
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add some products to your cart before checkout
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => {
                // Navigate to products - ProductCatalog will handle correct mode based on user/store context
                navigate('/products');
              }}
            >
              Browse Products
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => {
              // Navigate back to cart - cart page will automatically load correct cart based on user
              navigate('/cart');
            }}
            disabled={orderPlaced}
          >
            Back to Cart
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Checkout
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stepper */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Step Content */}
      <Box sx={{ mb: 3 }}>
        {getStepContent(activeStep)}
      </Box>

      {/* Navigation Buttons */}
      {activeStep < 3 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            variant="outlined"
          >
            Back
          </Button>

          <Button
            variant="contained"
            onClick={activeStep === steps.length - 2 ? handlePlaceOrder : handleNext}
            disabled={!validateStep(activeStep) || processing}
          >
            {activeStep === steps.length - 2 ? (
              processing ? 'Processing...' : 'Place Order'
            ) : (
              'Next'
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Checkout;